import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Bot, Globe, LayoutDashboard, Server, ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { mockData } from '../data/mock';

const iconMap = {
  1: Bot,
  2: Globe,
  3: LayoutDashboard,
  4: Server
};

const Services = () => {
  const { services } = mockData;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="services" className="py-20 md:py-32 bg-accent/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge className="mb-4 bg-blue-600/10 text-blue-600 border-blue-600/20">
            Services
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
            How I Help Your Business Grow
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 max-w-3xl mx-auto">
            From intelligent automation to scalable infrastructure, I deliver solutions that drive real results
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
        >
          {services.map((service) => {
            const Icon = iconMap[service.id];
            return (
              <motion.div key={service.id} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-all duration-300 border-border hover:border-blue-600/50 group">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 bg-blue-600/10 rounded-lg group-hover:bg-blue-600 transition-colors duration-300">
                        <Icon className="h-6 w-6 text-blue-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <ArrowRight className="h-5 w-5 text-foreground/40 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                    <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground/60 mb-2">Problem</h4>
                      <p className="text-sm text-foreground/80">{service.problem}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground/60 mb-2">Solution</h4>
                      <p className="text-sm text-foreground/80">{service.solution}</p>
                    </div>
                    <div className="pt-4 border-t border-border">
                      <h4 className="text-sm font-semibold text-blue-600 dark:text-blue-500 mb-2 flex items-center">
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Outcome
                      </h4>
                      <p className="text-sm font-medium text-foreground">{service.outcome}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {service.features.map((feature, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;